import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import { t, tKey } from 'ember-intl';
import SettingsBudgetForecastModalBulkUpdateDateOrMonthRange from 'lumen/components/settings/budget-forecast/modal-bulk-update/date-or-month-range';
import {
  DATA_TYPE_TRANSLATIONS,
  DATA_TYPES,
  type DateGranularity,
} from 'lumen/utils/constants/budget-forecast-constants';

const DATA_SOURCES = {
  OTB: DATA_TYPES.OTB,
  FINAL_LY: DATA_TYPES.FINAL_LY,
  FINAL_2LY: DATA_TYPES.FINAL_2LY,
  BUDGET_LY: 'budget_ly',
  BUDGET: DATA_TYPES.BUDGET,
  FORECAST: DATA_TYPES.FORECAST,
  BI_FORECAST: DATA_TYPES.BI_FORECAST,
  FINANCE_FORECAST: DATA_TYPES.FINANCE_FORECAST,
  RMS_FORECAST: DATA_TYPES.RMS_FORECAST,
} as const;
type DataSource = (typeof DATA_SOURCES)[keyof typeof DATA_SOURCES];

const DATA_SOURCES_REQUIRING_DATA_ITEMS: DataSource[] = [
  DATA_TYPES.BI_FORECAST,
  DATA_TYPES.FINANCE_FORECAST,
  DATA_TYPES.RMS_FORECAST,
];

const DATA_SOURCE_TRANSLATIONS: Record<DataSource, string> = {
  [DATA_SOURCES.OTB]: tKey('settings.budget_forecast.current_otb'),
  [DATA_SOURCES.FINAL_LY]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.FINAL_LY],
  [DATA_SOURCES.FINAL_2LY]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.FINAL_2LY],
  [DATA_SOURCES.BUDGET]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.BUDGET],
  [DATA_SOURCES.BUDGET_LY]: tKey('settings.budget_forecast.last_year_budget'),
  [DATA_SOURCES.FORECAST]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.FORECAST],
  [DATA_SOURCES.BI_FORECAST]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.BI_FORECAST],
  [DATA_SOURCES.FINANCE_FORECAST]:
    DATA_TYPE_TRANSLATIONS[DATA_TYPES.FINANCE_FORECAST],
  [DATA_SOURCES.RMS_FORECAST]: DATA_TYPE_TRANSLATIONS[DATA_TYPES.RMS_FORECAST],
};

export interface FillFromExistingOptions {
  dataSource: DataSource;
  endDate: string;
  startDate: string;
}

interface SettingsBudgetForecastModalBulkUpdateFillFromExistingSignature {
  Args: {
    onOptionsChange: (options: Partial<FillFromExistingOptions>) => void;
    options: FillFromExistingOptions;
    /**
     * The granularity and date range that's selected on the settings page.
     */
    selectedDateRange: {
      endDate: string;
      granularity: DateGranularity;
      startDate: string;
    };
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastModalBulkUpdateFillFromExisting extends Component<SettingsBudgetForecastModalBulkUpdateFillFromExistingSignature> {
  @service declare budgetForecast: Services['budget-forecast'];
  @service declare intl: Services['intl'];

  get allowedDataSources(): DataSource[] {
    return (Object.keys(DATA_SOURCE_TRANSLATIONS) as DataSource[]).filter(
      (dataSource) => {
        if (DATA_SOURCES_REQUIRING_DATA_ITEMS.includes(dataSource)) {
          const hasDataSourceItems = this.budgetForecast.items.some(
            (item) => item.dataType === dataSource,
          );

          return hasDataSourceItems;
        }

        if (dataSource === 'budget' || dataSource === 'forecast') {
          const isSameAsView = this.args.view === dataSource;
          return !isSameAsView;
        }

        return true;
      },
    );
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get dataSourceOptions(): { id: DataSource; label: string }[] {
    return this.allowedDataSources.map((key) => {
      return {
        id: key,
        label: this.intl.t(DATA_SOURCE_TRANSLATIONS[key]),
      };
    });
  }

  get selectedDataSourceOption():
    | (typeof this.dataSourceOptions)[number]
    | undefined {
    return this.dataSourceOptions.find(
      (option) => option.id === this.args.options.dataSource,
    );
  }

  @action
  onDataSourceChange(option: (typeof this.dataSourceOptions)[number]): void {
    this.args.onOptionsChange({ dataSource: option.id });
  }

  @action
  onRangeChange(startDate: string, endDate: string): void {
    this.args.onOptionsChange({ startDate, endDate });
  }

  <template>
    <div class="flex flex-col gap-400">
      <div
        class="rounded-200 border-neutral-default bg-neutral-subdued border border-solid p-400"
      >
        <SettingsBudgetForecastModalBulkUpdateDateOrMonthRange
          @endDate={{@options.endDate}}
          @granularity={{@selectedDateRange.granularity}}
          @maxDate={{@selectedDateRange.endDate}}
          @minDate={{@selectedDateRange.startDate}}
          @onRangeChange={{this.onRangeChange}}
          @startDate={{@options.startDate}}
        />
      </div>

      <div class="flex flex-col gap-100">
        <p class="text-200--bold">
          {{t "budget_forecast.fill_from_existing.select_data_source"}}
        </p>
        <PrismSelectInput
          @onChange={{this.onDataSourceChange}}
          @options={{this.dataSourceOptions}}
          @value={{this.selectedDataSourceOption}}
        />
      </div>
    </div>
  </template>
}
